// @flow
import {
  Button,
  Icon,
  TextBlock,
  formatCentsAsDollars,
  formatDollarsForDisplay,
} from 'bench-internal-theme';
import { colors } from 'bench-style-constants';
import { ClientIdContext } from 'bk-web-app';
import { AppSidebar } from 'bk-web-app/app-sidebar';
import { LedgerAccountSidebar } from 'bk-web-app/ledger-account-sidebar';
// $FlowFixMe - TS file
import env from 'bench-environment';
import React, { Fragment } from 'react';
import styled from 'styled-components';

import FinancialPackageNotesHeaderContainer from './FinancialPackageNotesHeaderContainer';
import type { LedgerAccountCategory } from './types';

const FinancialPackageNotesLayout = styled.div`
  bottom: 0;
  left: 0;
  overflow: auto;
  padding: 32px;
  position: absolute;
  right: 0;
  top: 0;
`;

const NotesGrid = styled.div`
  display: grid;
  grid-template-columns: 84px 1fr;
  grid-row-gap: 8px;
`;

const RowLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(200px, 350px) 72px 140px minmax(300px, 500px) repeat(2, 100px);
`;

const HeaderLayout = styled.div`
  padding-top: 36px;
`;

const HeaderCell = styled.div`
  padding: 12px 24px 0 24px;
  ${({ borderRight }) => borderRight && `border-right: 1px solid ${colors.silt};`};
`;

const HeaderBalanceCell = styled(HeaderCell)`
  text-align: right;
`;

const Cell = styled.div`
  border-top: 1px solid ${colors.silt};
  padding: 8px 24px;
  ${({ borderRight }) => borderRight && `border-right: 1px solid ${colors.silt};`};
`;

const LedgerNameCell = styled(Cell)`
  padding-left: 0;
`;

const BalanceCell = styled(Cell)`
  text-align: right;
`;

const IconCell = styled(Cell)`
  padding: 4px 24px;
`;

const CategoryCell = styled(Cell)`
  border-top: none;
  padding: 36px 0 8px 0;
  ${({ borderRight }) => borderRight && `border-right: 1px solid ${colors.silt};`};
`;

const CategoryBalanceCell = styled(CategoryCell).attrs(() => ({
  borderRight: true,
}))`
  padding-right: 24px;
  padding-left: 24px;
  text-align: right;
`;

const LedgerRow = styled(RowLayout)`
  ${({ hasBackgroundColor }) => hasBackgroundColor && `background-color: ${colors.shell}`};
  ${({ isHighlighted }) => isHighlighted && `background: ${colors.brook};`};
  ${({ isLastRow }) => isLastRow && `border-bottom: none;`};
  cursor: pointer;

  &:hover {
    background: ${colors.brook};
  }
`;

type FinancialPackageNotesProps = {
  endDate: string,
  fiscalYear: number,
  hashtagFiscalYearTag: string,
  ledgerAccountCategories: Array<LedgerAccountCategory>,
  ledgerAccountCategoryTotalsMap: Map<string, ?number>,
  hashtagPeerReviewTag: string,
  hashtagResolvedTag: string,
  startDate: string,
};

const FinancialPackageNotesView = ({
  endDate,
  fiscalYear,
  hashtagFiscalYearTag,
  hashtagPeerReviewTag,
  hashtagResolvedTag,
  ledgerAccountCategories,
  ledgerAccountCategoryTotalsMap,
  startDate,
}: FinancialPackageNotesProps) => (
  <ClientIdContext.Consumer>
    {clientId => (
      <FinancialPackageNotesLayout>
        <FinancialPackageNotesHeaderContainer
          endDate={endDate}
          fiscalYear={fiscalYear}
          hashtagFiscalYearTag={hashtagFiscalYearTag}
          startDate={startDate}
        />
        <HeaderLayout>
          <RowLayout>
            <HeaderCell>{/* ledger name */}</HeaderCell>
            <HeaderCell>{/* Ledger sidebar icon */}</HeaderCell>
            <HeaderBalanceCell borderRight>
              <TextBlock color={colors.dust} styleType="small" uppercase>
                Balance
              </TextBlock>
            </HeaderBalanceCell>
            <HeaderCell borderRight>
              <NotesGrid>
                <TextBlock color={colors.dust} styleType="small" uppercase>
                  Tag
                </TextBlock>
                <TextBlock color={colors.dust} styleType="small" uppercase>
                  Notes Visible to Client (in YEF Package)
                </TextBlock>
              </NotesGrid>
            </HeaderCell>
            <HeaderCell>
              <TextBlock color={colors.dust} styleType="small" uppercase>
                Peer Review ({hashtagPeerReviewTag})
              </TextBlock>
            </HeaderCell>
            <HeaderCell>
              <TextBlock color={colors.dust} styleType="small" uppercase>
                Resolved ({hashtagResolvedTag})
              </TextBlock>
            </HeaderCell>
          </RowLayout>
        </HeaderLayout>
        {/* Hide P/B category from this view */}
        {ledgerAccountCategories
          .filter(category => category.id !== '9')
          .map(ledgerAccountCategory => (
            <Fragment key={ledgerAccountCategory.id}>
              <RowLayout>
                <CategoryCell>
                  <TextBlock styleType="mediumBold">
                    {ledgerAccountCategory.name} ({ledgerAccountCategory.ledgerAccounts.length})
                  </TextBlock>
                </CategoryCell>
                <CategoryCell>{/* Ledger sidebar icon */}</CategoryCell>
                <CategoryBalanceCell>
                  <TextBlock styleType="smallBold">
                    {formatDollarsForDisplay(
                      formatCentsAsDollars(
                        ledgerAccountCategoryTotalsMap.get(ledgerAccountCategory.id) || 0,
                      ),
                    )}
                  </TextBlock>
                </CategoryBalanceCell>
                <CategoryCell borderRight>{/* Notes */}</CategoryCell>
                <CategoryCell>{/* Peer Review */}</CategoryCell>
                <CategoryCell>{/* Resolved */}</CategoryCell>
              </RowLayout>
              <AppSidebar>
                {({ closeSidebar, openSidebar, openerMeta }) => {
                  const selectedLedgerAccount =
                    // eslint-disable-next-line no-underscore-dangle
                    openerMeta && openerMeta.__typename === 'LedgerAccount'
                      ? openerMeta
                      : undefined;

                  const toggleLedgerAccount = ledgerAccount => {
                    if (ledgerAccount && ledgerAccount !== openerMeta) {
                      openSidebar(
                        <LedgerAccountSidebar
                          dismiss={closeSidebar}
                          ledgerAccountId={ledgerAccount.id}
                        />,
                        ledgerAccount,
                      );
                    } else {
                      closeSidebar();
                    }
                  };

                  return ledgerAccountCategory.ledgerAccounts.map((ledgerAccount, index) => {
                    const isLastRow = index === ledgerAccountCategory.ledgerAccounts.length - 1;
                    const ledgerAccountTotal =
                      ledgerAccount.closingBalanceInCents || ledgerAccount.sumTotalInCents;
                    const isHighlighted =
                      (selectedLedgerAccount && selectedLedgerAccount.id) === ledgerAccount.id;
                    return (
                      <LedgerRow
                        key={ledgerAccount.id}
                        data-bypass-dismiss-sidebar
                        hasBackgroundColor={index % 2}
                        isHighlighted={isHighlighted}
                        isLastRow={isLastRow}
                        onMouseDown={() => toggleLedgerAccount(ledgerAccount)}
                      >
                        <LedgerNameCell>
                          <TextBlock styleType="small">
                            <a
                              href={`${env.location.bkTools}/${clientId}/general-ledger`}
                              rel="noopener noreferrer"
                              target="_blank"
                            >
                              {ledgerAccount.name}
                            </a>
                          </TextBlock>
                        </LedgerNameCell>
                        <IconCell>
                          <Button icon="information" size="small" styleType="total-ghost" />
                        </IconCell>
                        <BalanceCell borderRight>
                          <TextBlock styleType="small">
                            {formatDollarsForDisplay(formatCentsAsDollars(ledgerAccountTotal || 0))}
                          </TextBlock>
                        </BalanceCell>
                        <Cell borderRight>
                          <NotesGrid>
                            {ledgerAccount.internalNotes.map(internalNote => (
                              <Fragment key={internalNote.id}>
                                <TextBlock styleType="small" uppercase>
                                  {hashtagFiscalYearTag}
                                </TextBlock>
                                <TextBlock color={colors.smoke} styleType="small">
                                  {internalNote.body}
                                </TextBlock>
                              </Fragment>
                            ))}
                          </NotesGrid>
                        </Cell>
                        <IconCell>
                          {ledgerAccount.isPeerReviewed && (
                            <Icon color={colors.dust} icon="speechBubble" size="small" />
                          )}
                        </IconCell>
                        <IconCell>
                          {ledgerAccount.isResolved && (
                            <Icon color={colors.bonsai} icon="circleCheck" size="small" />
                          )}
                        </IconCell>
                      </LedgerRow>
                    );
                  });
                }}
              </AppSidebar>
            </Fragment>
          ))}
      </FinancialPackageNotesLayout>
    )}
  </ClientIdContext.Consumer>
);

export default FinancialPackageNotesView;
